import { FC, ReactNode, useEffect, useState } from "react";
import { Box, Divider, Typography } from "@mui/material";
import {
  ArrowDownward,
  Block,
  Cancel,
  CheckCircle,
  Help,
  HideImage,
  NetworkPing,
  PauseCircleOutline,
  Videocam,
} from "@mui/icons-material";
import { Score } from "@/models/pdfData";
import { gameState, useTeamSides } from "@/services/gameState";
import { useAppDataContext } from "@context/AppContext";
import { LastChallenge, SamsChallengeType } from "@/models/gameState";
import { useGameDataContext } from "@context/GameDataContext";

const RefereeTabletChallengeInfo: FC<{ side: "left" | "right" }> = ({
  side,
}) => {
  const isChallengeOngoing = gameState("isChallengeOngoing");
  const lastChallenge = gameState("lastChallenge");
  const lastAction = gameState("lastAction");
  const scoreConfiguration = gameState("scoreConfiguration");
  const { addChallengeCounter } = useGameDataContext();
  const currentSet = gameState("currentSet");
  const { leftTeam, rightTeam } = useTeamSides(useAppDataContext());

  const teamReference = side === "left" ? leftTeam : rightTeam;

  const [currentTimestamp, setCurrentTimestamp] = useState<number>(Date.now());

  useEffect(() => {
    const timer = setInterval(
      () => {
        setCurrentTimestamp(Date.now());
      },
      isChallengeOngoing ? 100 : 50 * 1000,
    );
    return () => {
      clearInterval(timer);
    };
  }, [isChallengeOngoing]);

  if (
    lastChallenge?.teamCode !== teamReference ||
    (!isChallengeOngoing && !lastAction?.type.startsWith("[Challenges]"))
  ) {
    return (
      <Box
        onClick={() => {
          if (!scoreConfiguration.videoChallengeAvailable) {
            addChallengeCounter(teamReference, currentSet?.setNumber || 1);
          }
        }}
      />
    );
  }

  const renderScore = (score?: Score) => {
    const ScoreTypo: FC<{ bold: boolean; text: string | number }> = ({
      bold,
      text,
    }) => (
      <Typography
        {...typographyProps}
        sx={{ fontWeight: bold ? "600" : "400" }}
      >
        {`${text}`.padStart(2, "0")}
      </Typography>
    );
    return (
      <Box display={"flex"} alignItems={"center"} columnGap={"1vw"}>
        <ScoreTypo
          bold={lastChallenge.teamThatLastScored === leftTeam}
          text={score ? score[leftTeam] : "--"}
        />
        <Typography {...typographyProps}>{":"}</Typography>
        <ScoreTypo
          bold={lastChallenge.teamThatLastScored === rightTeam}
          text={score ? score[rightTeam] : "--"}
        />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        py: "1vw",
        mx: "2vw",
        borderRadius: "1vw",
        border: "0.2vw solid #0000001f",
        minWidth: 0,
        rowGap: "1vw",
        boxShadow: "0px 0px 6px 1px #0000001a",
      }}
    >
      <Videocam
        sx={{
          "@keyframes pulse": {
            "0%": {
              opacity: 1,
            },
            "50%": {
              opacity: 0.3,
            },
            "100%": {
              opacity: 1,
            },
          },
          animation: isChallengeOngoing
            ? "pulse 2s ease-in-out infinite"
            : "unset",
        }}
      />
      <Typography {...typographyProps}>
        {ChallengeTypeTranslations[lastChallenge.challengeType] ||
          lastChallenge.challengeType}
      </Typography>
      <Box
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        width={"100%"}
        flexShrink={0}
        flexGrow={1}
      >
        <Box
          width={"50%"}
          display={"grid"}
          gridTemplateRows={"2fr 1fr auto 2fr 1fr"}
          justifyItems={"center"}
          alignItems={"self-end"}
          gap={"1vw"}
        >
          {renderChallengeMoment(lastChallenge)}
          <Typography {...typographyProps} fontSize={"1.8vw"}>
            (Zeitpunkt)
          </Typography>
          <Divider orientation={"horizontal"} flexItem sx={{ width: "100%" }} />
          {renderChallengeResultIcon(lastChallenge)}
          <Typography {...typographyProps} fontSize={"1.8vw"}>
            (Ergebnis)
          </Typography>
        </Box>
        <Divider orientation={"vertical"} flexItem />
        <Box
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"space-evenly"}
          alignItems={"center"}
          width={"50%"}
        >
          <Box display={"flex"} flexDirection={"column"} alignItems={"center"}>
            {renderScore(lastChallenge.scoreRallyStart)}
            <ArrowDownward fontSize={"small"} />
            {renderScore(lastChallenge.setScoreAfterChallenge)}
          </Box>
          <Typography {...typographyProps} fontSize={"1.8vw"}>
            (Score)
          </Typography>
        </Box>
      </Box>
      <Typography {...typographyProps}>
        Dauer:{" "}
        {Math.floor(
          (lastChallenge.durationMs ||
            currentTimestamp -
              lastChallenge.timestamp +
              lastChallenge.timeDelta) / 1000,
        )}
        s
      </Typography>
    </Box>
  );
};

const typographyProps = {
  fontSize: "2.5vw",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  maxWidth: "100%",
};

export const ChallengeTypeTranslations: { [key in SamsChallengeType]: string } =
  {
    ANTENNA_CONTACT: "Antenna Touch",
    BALL_IN_OUT: "Ball In/Out",
    BLOCK_TOUCH: "Block Touch",
    FOOT_FAULT_ATTACK_LINE: "Foot Fault (Attack)",
    FOOT_FAULT_BASE_LINE: "Foot Fault (Base)",
    FOOT_FAULT_HALFWAY_LINE: "Foot Fault (Center)",
    GROUND_CONTACT: "Floor touch",
    NET_FAULT: "Net Fault",
    LAST_TOUCH: "Last Touch",
  };

const renderChallengeResultIcon = (challenge: LastChallenge): ReactNode => {
  const { declined, unresolved, successful, durationMs } = challenge;

  if (declined) {
    return <Block />;
  } else if (unresolved) {
    return <HideImage />;
  } else if (successful) {
    return <CheckCircle color={"success"} />;
  } else if (
    !declined &&
    !unresolved &&
    !successful &&
    durationMs &&
    durationMs > 0
  ) {
    return <Cancel color={"error"} />;
  } else {
    return <Help sx={{ opacity: "0%" }} />;
  }
};

const renderChallengeMoment = (challenge: LastChallenge): ReactNode => {
  const { challengeMoment, teamThatLastScored, teamCode } = challenge;
  if (challengeMoment === "AFTER_RALLY") {
    return <NetworkPing />;
  } else if (
    challengeMoment === "DURING_RALLY" ||
    teamCode === teamThatLastScored
  ) {
    return <PauseCircleOutline />;
  } else {
    return <Box />;
  }
};

export default RefereeTabletChallengeInfo;
